import React, { useEffect, useState, useRef, useMemo } from 'react';
import './CobrarPedidos.scss';
import IndividualReceipt from './components/IndividualReceipt/IndividualReceipt';
import DteInfo from '../../../Caja/Receipts/components/Table/components/ReceiptDetails/components/DteInfo/DteInfo';
import RenderPdf from '../../../Caja/Receipts/components/Table/components/ReceiptDetails/components/DteInfo/components/DteCard/components/RenderPdf/RenderPdf';
import { Modal, Button } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { getPermissionState } from '../../../../../utils/functions';
import {
 getActivePaymentReceiptsByMesaId,
 getActivePaymentReceiptsByPedidoGroupId,
 createPaymentReceiptByPedidoIdV2,
 createPaymentReceiptByMesaIdV2,
 getPaymentReceiptByPedidoId,
 deleteActivePaymentReceiptsByMesaId
} from '../../../../../crud/payments_api/payment_receipts';
import { deactivatePedidoGroup } from '../../../../../crud/pedidos_api/pedido_groups';
import { toast } from 'react-toastify';
import MiniLoadingSpinner from '../../../../../components/Spinners/MiniLoadingSpinner';
import { usePedidos } from '../../PedidosContext';
import { updateSalones } from '../../../Configuracion/Salones/components/SalonDisplayer/components/functions/updateSalones';
import SplitMethod from './components/SplitMethod/SplitMethod';
import { getEnableQuickBoletaPrint } from '../../../../../crud/localDB/pedidosConfig';
import useSiiConfigured from '../../../../../crud/management_api/useSiiConfigured';
import { printBoletaForReceipt, BoletaPrintTarget } from './components/IndividualReceipt/modules/printBoleta';


interface CobrarPedidoProps {
  mesa_id?: number;
  pedido_group_id?: number;
  client_id?: number;
  client_name?: string;
  pedido?: any;
  allowSplit?: boolean;
  show: boolean;
  onHide: () => void;
  onClose: (targetSelections?: string[]) => void;
  mesa?: any;
  pedido_type?: string;
  setPedidos?: (pedidos: any) => void;
  targetSelections?: string[]; // NEW: Array of selection_ids to be paid
  acceptChange?: boolean; // NEW: if true, show cash payment modal for cash payments
  autoCreateSingle?: boolean; // NEW: if true, auto-create single receipt and skip SplitMethod UI
  preselectedSplitMethod?: 'equal' | 'custom'; // NEW: pre-select a split method from dropdown
}


const CobrarPedidos: React.FC<CobrarPedidoProps> = ({
 mesa_id,
 pedido_group_id,
 client_id,
 client_name,
 pedido,
 show,
 onHide,
 allowSplit,
 onClose,
 mesa,
 pedido_type,
 setPedidos: externalSetPedidos,
 acceptChange = false, // Default to false for backwards compatibility
 targetSelections,
 autoCreateSingle = false, // Default to false for backwards compatibility
 preselectedSplitMethod
}) => {
     const {pedidos,setPedidos} = usePedidos();
  const [goToPayment, setGoToPayment] = useState(false);
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [showSelector, setShowSelector] = useState(false);
  const [autoReceiptCreated, setAutoReceiptCreated] = useState(false);
  const [showDteModal, setShowDteModal] = useState(false);

  // One-click SII Boleta state
  const [quickBoletaEnabled, setQuickBoletaEnabled] = useState(false);
  const [printingBoleta, setPrintingBoleta] = useState(false);
  const [boletaToPrint, setBoletaToPrint] = useState<BoletaPrintTarget | null>(null);
  const printingBoletaRef = useRef(false);
  // Tracks whether the "all receipts paid" completion was already handled while deferring
  // the modal close (so we keep the screen open for boleta printing without re-running side-effects).
  const paidDeferHandledRef = useRef(false);

  const currentUserLocalId = useMemo(() => {
    const u = JSON.parse(localStorage.getItem('user') ?? '{}');
    return Number(u.local_id) || 0;
  }, []);
  const siiConfigured = useSiiConfigured(currentUserLocalId);
  const showQuickBoleta = quickBoletaEnabled && siiConfigured === true;
  const showQuickBoletaRef = useRef(showQuickBoleta);
  useEffect(() => {
    showQuickBoletaRef.current = showQuickBoleta;
  }, [showQuickBoleta]);

  useEffect(() => {
    getEnableQuickBoletaPrint().then(setQuickBoletaEnabled).catch(() => setQuickBoletaEnabled(false));
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!show) {
      setAutoReceiptCreated(false);
      setGoToPayment(false);
      setShowSelector(false);
      setReceipts([]);
      setCurrentIndex(0);
      setBoletaToPrint(null);
      paidDeferHandledRef.current = false;
    } else {
      // When modal opens, set loading to true
      setLoading(true);
    }
  }, [show]);

  // Recovery mechanism to restore receipt_ids from localStorage - run only once on mount
  useEffect(() => {
    const receiptCache = JSON.parse(localStorage.getItem('pedidoReceiptCache') || '{}');
    if (Object.keys(receiptCache).length > 0) {
      const restoreReceiptIds = (prevPedidos: any) => {
        return prevPedidos.map((p: any) => {
          if (receiptCache[p.pedido_id] && !p.receipt_id) {
            return { ...p, receipt_id: receiptCache[p.pedido_id] };
          }
          return p;
        });
      };
      
      if (externalSetPedidos) {
        externalSetPedidos(restoreReceiptIds);
      } else {
        setPedidos(restoreReceiptIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount


  useEffect(() => {
    // GUARD: If we already have receipts and goToPayment is true, don't refetch
    // This prevents the race condition when mesa.charged updates after receipt creation
    if (goToPayment && receipts.length > 0) {
      setLoading(false);
      return;
    }

    // Handle pedido groups - allow split method selection
    if (pedido_group_id !== undefined) {
      getActivePaymentReceiptsByPedidoGroupId(pedido_group_id).then((response) => {
        if (response.data.length === 0) {
          // No existing receipts - show split method selector
          setShowSelector(true);
          setLoading(false);
        } else {
          // Existing receipts found - show them
          setShowSelector(false);
          setReceipts(response.data);
          setCurrentIndex(0);
          setGoToPayment(true);
          setLoading(false);
        }
      }).catch((error) => {
        console.error('Error getting active payment receipts for group: ', error);
        setShowSelector(true);
        setLoading(false);
      });
      return;
    }

    // Handle mesas
    if (mesa_id === undefined) return;
      if (mesa_id !== undefined) {
          getActivePaymentReceiptsByMesaId(mesa_id).then((response) => {
            if (response.data.length === 0) {
                // Check if autoCreateSingle is true (from split button primary action)
                if (autoCreateSingle) {
                  // Automatically create single receipt
                  const user = JSON.parse(localStorage.getItem('user') ?? '{}');
                  const user_id = user.user_id ? Number(user.user_id) : 0;
                  
                  createPaymentReceiptByMesaIdV2(mesa_id, 'single', user_id, 1).then((receiptResponse: any) => {
                    const mesasPaymentCache = JSON.parse(localStorage.getItem('mesasPaymentCache') || '{}');
                    if (mesa) mesa.charged = true;
                    setReceipts(receiptResponse.data);
                    mesasPaymentCache[mesa_id] = {
                      splitMethod: 'single',
                      totalPersons: 1,
                      paidPersons: 0
                    };
                    localStorage.setItem('mesasPaymentCache', JSON.stringify(mesasPaymentCache));
                    setCurrentIndex(0);
                    // IMPORTANT: Set goToPayment BEFORE dispatching event to prevent race condition
                    setGoToPayment(true);
                    setShowSelector(false);
                    setLoading(false);
                    // Dispatch event to update UI in real-time (after state is set)
                    window.dispatchEvent(new CustomEvent('mesa-receipt-generated', { detail: { mesa_id } }));
                  }).catch((error: any) => {
                    console.error('Error auto-creating single receipt:', error);
                    toast.error('Error al generar recibo');
                    setShowSelector(true);
                    setLoading(false);
                  });
                } else if (mesa && mesa.is_virtual) {
                  // Automatically create receipt for virtual table
                  const user = JSON.parse(localStorage.getItem('user') ?? '{}');
                  const user_id = user.user_id ? Number(user.user_id) : 0;
                  
                  createPaymentReceiptByMesaIdV2(mesa_id, 'single', user_id, 1).then((receiptResponse: any) => {
                    // Refetch the receipt to get complete data with selections
                    getActivePaymentReceiptsByMesaId(mesa_id).then((refetchResponse) => {
                      setReceipts(refetchResponse.data);
                      setCurrentIndex(0);
                      // IMPORTANT: Set goToPayment BEFORE dispatching event to prevent race condition
                      setGoToPayment(true);
                      setShowSelector(false);
                      setLoading(false);
                      // Dispatch event to update UI in real-time (after state is set)
                      window.dispatchEvent(new CustomEvent('mesa-receipt-generated', { detail: { mesa_id } }));
                    }).catch((refetchError: any) => {
                      // Fallback to using the response data even if refetch fails
                      setReceipts([receiptResponse.data]);
                      setCurrentIndex(0);
                      // IMPORTANT: Set goToPayment BEFORE dispatching event to prevent race condition
                      setGoToPayment(true);
                      setShowSelector(false);
                      setLoading(false);
                      // Dispatch event to update UI in real-time (after state is set)
                      window.dispatchEvent(new CustomEvent('mesa-receipt-generated', { detail: { mesa_id } }));
                    });
                  }).catch((error: any) => {
                    console.error('Error auto-creating receipt for virtual table:', error);
                    toast.error('Error al generar cuenta para mesa virtual');
                    setShowSelector(true);
                    setLoading(false);
                  });
                } else {
                  // Regular table - show split method selector
                  setShowSelector(true);
                  setLoading(false);
                  // Aseguramos que la mesa esté lista para crear un nuevo recibo
                  if (mesa) {
                    mesa.charged = false;
                  }
                }
            } else {
                setShowSelector(false);
                setReceipts(response.data);
                setCurrentIndex(0);
                setGoToPayment(true);
                setLoading(false);
                //setReceiptInfoRetrieved(r => [true, r[1]]);
            }
          }).catch((error) => {
              console.error('Error getting active payment receipts: ', error);
          });
      } else {
        setShowSelector(true);
        //setReceiptInfoRetrieved(r => [true, r[1]]);
        setLoading(false);
      }
  }, [mesa_id, mesa, pedido_group_id, client_name, goToPayment, receipts.length, autoCreateSingle]);


  useEffect(() => {
    // Only run when modal is shown (user clicked Ver Precuenta / Generar Precuenta)
    if (!show) return;
    
    // Handle delivery/caja/autoservicio pedidos
    if (pedido && (pedido_type === "Delivery" || pedido_type === "Caja" || pedido_type === "Autoservicio")) {
      // If pedido is paid or has a receipt_id, fetch the existing receipt
      if (pedido.payed || pedido.receipt_id) {
        setLoading(true);
        getPaymentReceiptByPedidoId(pedido.pedido_id).then((response) => {
          // The API returns the receipt array directly in response.data
          const receiptsArray = response.data;
          
          if (receiptsArray && Array.isArray(receiptsArray) && receiptsArray.length > 0) {
            // Ensure each receipt has selections array (defensive programming)
            const validatedReceipts = receiptsArray.map((r: any) => ({
              ...r,
              selections: r.selections || []
            }));
            
            setReceipts(validatedReceipts);
            setGoToPayment(true);
            setShowSelector(false);
          } else {
            // No receipt found, allow creation
            setShowSelector(true);
          }
        }).catch((error) => {
          console.error('Error getting payment receipt by pedido ID: ', error);
          // On error, allow creation flow
          setShowSelector(true);
        }).finally(() => {
          setLoading(false);
        });
      } else {
        // For unpaid delivery/caja orders, immediately trigger automatic creation flow
        setShowSelector(true);
        setLoading(false);
      }
    }
  }, [pedido, pedido_type, show]);

  // Separate effect for automatic receipt creation for delivery/caja orders
  useEffect(() => {
    if (show &&
        showSelector && 
        (pedido_type === "Delivery" || pedido_type === "Caja" || pedido_type === "Autoservicio") && 
        receipts.length === 0 && 
        client_name !== undefined &&
        !goToPayment &&
        !autoReceiptCreated &&
        !pedido?.receipt_id && // Don't create if receipt already exists
        !pedido?.payed) { // Don't create if pedido is already paid
      
      setAutoReceiptCreated(true); // Prevent multiple executions
      
      const user = JSON.parse(localStorage.getItem('user') ?? '{}');
      const user_id = user.user_id ? Number(user.user_id) : 0;
      setLoading(true);
      
      createPaymentReceiptByPedidoIdV2(pedido.pedido_id, 'single', user_id, client_id, client_name, pedido_type).then((response) => {
        if (response.status === 200 || response.status === 201) {
          setReceipts(response.data);
          const receipt_id = response?.data ? response.data[0].receipt_id : undefined;
          
          // More robust state update with immediate callback
          const updatePedidoWithReceipt = (prevPedidos: any) => {
            return prevPedidos.map((p: any) => {
                if (p.pedido_id === pedido.pedido_id) {
                    // Store in localStorage as backup
                    const receiptCache = JSON.parse(localStorage.getItem('pedidoReceiptCache') || '{}');
                    receiptCache[pedido.pedido_id] = receipt_id;
                    localStorage.setItem('pedidoReceiptCache', JSON.stringify(receiptCache));
                    return { ...p, receipt_id: receipt_id };
                }
                return p;
            });
          };
          
          // Use functional update to avoid stale state
          if (externalSetPedidos) {
              externalSetPedidos(updatePedidoWithReceipt);
          } else {
              setPedidos(updatePedidoWithReceipt);
          }
          
          // Additional delayed update to ensure persistence against race conditions
          setTimeout(() => {
            const delayedUpdate = (prevPedidos: any) => {
              return prevPedidos.map((p: any) => {
                  if (p.pedido_id === pedido.pedido_id) {
                      return { ...p, receipt_id: receipt_id };
                  }
                  return p;
              });
            };
            if (externalSetPedidos) {
                externalSetPedidos(delayedUpdate);
            } else {
                setPedidos(delayedUpdate);
            }
          }, 500); // Increased delay to 500ms
          
          setGoToPayment(true);
          setShowSelector(false);
        }
      }).catch((error:any) => {
          console.error('Error creating payment receipt: ', error);
          toast.error('Error creando recibo de pago');
          setGoToPayment(false);
          setAutoReceiptCreated(false); // Reset on error
      }).finally(() => {
          setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, showSelector, pedido_type, receipts.length, client_name, goToPayment, pedido?.pedido_id, pedido?.receipt_id, pedido?.payed, client_id, autoReceiptCreated]);


  // Runs the "all receipts fully paid" completion logic (group deactivation, salones update,
  // mesa flag updates). When closeUI is false the modal is intentionally kept open so the
  // cashier can print a boleta; the existing side-effects still run so backend/table state stays correct.
  const finalizePaidReceipts = (closeUI: boolean) => {
    const mesasPaymentCache = JSON.parse(localStorage.getItem('mesasPaymentCache') || '{}');
    const cachedMethod = mesasPaymentCache[mesa_id || 0]?.splitMethod;

    // Handle pedido groups - deactivate group when fully paid
    if (pedido_group_id !== undefined) {
      // Only deactivate once - check if we've already processed this
      const processedKey = `group_payment_processed_${pedido_group_id}`;
      if (sessionStorage.getItem(processedKey)) {
        return; // Already processed, skip
      }

      // Mark as processed immediately to prevent double execution
      sessionStorage.setItem(processedKey, 'true');

      deactivatePedidoGroup(pedido_group_id)
        .then(() => {
          // Trigger event to refresh groups list
          window.dispatchEvent(new CustomEvent('pedido-group-deactivated'));
          // Clear the processed flag after successful completion
          sessionStorage.removeItem(processedKey);
          if (closeUI) onClose(); // Close the modal and reset view
        })
        .catch(() => {
          // Clear the processed flag on error so user can retry
          sessionStorage.removeItem(processedKey);
          // Still close even if deactivation fails
          if (closeUI) onClose();
        });
      return;
    }

    // IMPORTANT: If targetSelections was provided, this is a partial item payment.
    // We should NOT close the table - there are still unpaid items on the table.
    // Just close the payment modal and let the user continue.
    if (targetSelections && targetSelections.length > 0) {
      if (closeUI) onClose(targetSelections); // Pass targetSelections to indicate partial payment
      return;
    }

    // Determine if table should close BEFORE calling updateSalones
    // (updateSalones modifies the cache, so we check first)
    const cacheEntry = mesasPaymentCache[mesa_id || 0];
    const shouldCloseMesa = !cacheEntry || // No cache = single payment, close it
                            cachedMethod === 'single' || // Single method always closes
                            !cachedMethod || // No cached method = single payment
                            (cacheEntry && (cacheEntry.paidPersons + 1) >= cacheEntry.totalPersons); // All persons will be paid after this

    // Update salones state - the updateSalones function handles the logic
    // to determine if the table should be marked as unused based on:
    // - split method (single, equal, custom)
    // - number of paid persons vs total persons (from mesasPaymentCache)
    if (mesa_id !== undefined) {
      updateSalones(mesa_id, { event: 'table_closed', data: { mesa_id } }, () => {});
    }

    // Update the mesa object directly for immediate UI feedback
    if (mesa !== undefined && shouldCloseMesa) {
      mesa.used = false;
      mesa.charged = false;
      mesa.total_charges = 0;
    }

    if (closeUI) onClose();
  };

  useEffect(() => {
    if (receipts?.every((receipt) => receipt.payed_full) && receipts.length > 0) {
      const isPartialPayment = !!(targetSelections && targetSelections.length > 0);
      if (showQuickBoletaRef.current && !isPartialPayment) {
        // Feature on (full payment): run completion side-effects once but keep the modal open so
        // the cashier can print the boleta. They close via the parent modal's close button.
        // Partial payments are NOT deferred so their normal flow (onClose with selections) runs.
        if (paidDeferHandledRef.current) return;
        paidDeferHandledRef.current = true;
        finalizePaidReceipts(false);
      } else {
        // Default behavior (feature off, or partial payment): close immediately as before.
        finalizePaidReceipts(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipts, currentIndex, onClose, mesa_id, mesa, pedido_group_id, targetSelections]);

  // Keyboard navigation for carousel
  useEffect(() => {
    if (!goToPayment || receipts.length <= 1) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (currentIndex < receipts.length - 1) {
            setCurrentIndex(currentIndex + 1);
          }
          break;
        case 'Home':
          event.preventDefault();
          setCurrentIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setCurrentIndex(receipts.length - 1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, receipts.length, goToPayment]);

  // Touch/swipe support for mobile
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd || isTransitioning) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentIndex < receipts.length - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setTimeout(() => setIsTransitioning(false), 100);
      }, 150);
    }
    
    if (isRightSwipe && currentIndex > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex - 1);
        setTimeout(() => setIsTransitioning(false), 100);
      }, 150);
    }
  };

  // DTE Generation handler
  const handleGenerateDte = () => {
    setShowDteModal(true);
  };

  // One-click SII Boleta: generate (or reuse) the boleta and open the 80mm PDF ready to print.
  const handlePrintBoleta = async () => {
    const current = receipts[currentIndex];
    if (!current?.receipt_id) return;
    if (printingBoletaRef.current) return; // guard against rapid double-clicks
    printingBoletaRef.current = true;
    setPrintingBoleta(true);
    try {
      const localIdForReceipt = current.local_id ?? currentUserLocalId;
      const target = await printBoletaForReceipt(localIdForReceipt, current.receipt_id);
      setBoletaToPrint(target);
    } catch (error: any) {
      toast.error(error?.message || 'No fue posible generar la boleta');
    } finally {
      printingBoletaRef.current = false;
      setPrintingBoleta(false);
    }
  };

  const handleDelete = () => {
    if (mesa === undefined) return;
    
    deleteActivePaymentReceiptsByMesaId(mesa.id).then((response) => {
      toast.success('Recibo anulado correctamente');
      setReceipts([]);
      mesa.charged = false;
      setGoToPayment(false);
      onHide();
      window.location.reload();
    }).catch((error) => {
      console.error('Error deleting active payment receipts: ', error);
      toast.error('Error anulando el recibo');
    });
  };

  if (loading) {
      return <MiniLoadingSpinner phrases={[]} left='50%' />;
  }

  return (
      <div className='cobrar-pedidos-container'>
          {(!goToPayment && allowSplit && showSelector && (!mesa || !mesa.charged || pedido_group_id !== undefined)) && (
            <SplitMethod
              mesa_id={mesa_id}
              pedido_group_id={pedido_group_id}
              client_id={client_id}
              client_name={client_name}
              pedido={pedido}
              mesa={mesa}
              pedido_type={pedido_type}
              externalSetPedidos={externalSetPedidos}
              pedidos={pedidos}
              setPedidos={setPedidos}
              setReceipts={setReceipts}
              setGoToPayment={setGoToPayment}
              targetSelections={targetSelections}
              onHide={onHide}
              hideUnicaOption={!!preselectedSplitMethod}
              preselectedMethod={preselectedSplitMethod}
            />
          )}
          {goToPayment && receipts?.length > 0 && (
            <>
                <div 
                  className={`receipt-container animate ${isTransitioning ? 'transitioning' : ''}`}
                  key={receipts[currentIndex].receipt_id || currentIndex}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {(() => {
                      return (
                          <IndividualReceipt
                              receipt={receipts[currentIndex]}
                              receipts={receipts}
                              setReceipts={setReceipts}
                              setCurrentIndex={setCurrentIndex}
                              externalSetPedidos={externalSetPedidos}
                              // Pass the delete function and show condition to IndividualReceipt
                              onDelete={(goToPayment && mesa !== undefined  && getPermissionState(131)) ? handleDelete : undefined}
                              acceptChange={acceptChange}
                              onClose={onClose}
                              targetSelections={targetSelections}
                              onGenerateDte={handleGenerateDte}
                              showDteQuickAction={true}
                              onPrintBoleta={handlePrintBoleta}
                              showQuickBoleta={showQuickBoleta}
                              printingBoleta={printingBoleta}
                          />)
                      ;
                  })()}
                </div>
        
            </>
          )}
                {/* DTE Modal - Full DTE management (create, list, print) */}
                <Modal 
                  show={showDteModal} 
                  onHide={() => setShowDteModal(false)} 
                  centered 
                  size="lg"
                  className="dte-management-modal"
                >
                  <Modal.Header>
                    <div className="form-modal-title-container">
                      <Modal.Title className="form-modal-title">Documentos Tributarios</Modal.Title>
                    </div>
                    <Button variant="none" className="modal-close-button" onClick={() => setShowDteModal(false)}>
                      <X size={24} />
                    </Button>
                  </Modal.Header>
                  <Modal.Body>
                    {receipts[currentIndex]?.receipt_id && (
                      <DteInfo 
                        receiptId={receipts[currentIndex].receipt_id} 
                        localId={receipts[currentIndex].local_id} 
                      />
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <div className="form-modal-footer-custom">
                      <Button variant="none" className="form-button-cancel" onClick={() => setShowDteModal(false)}>Cerrar</Button>
                    </div>
                  </Modal.Footer>
                </Modal>

                {/* One-click SII Boleta: 80mm PDF auto-opens and triggers the print dialog */}
                {boletaToPrint && (
                  <RenderPdf
                    show={!!boletaToPrint}
                    onHide={() => setBoletaToPrint(null)}
                    dteId={boletaToPrint.dteId}
                    localId={boletaToPrint.localId}
                    folio={boletaToPrint.folio}
                    tipoDte={boletaToPrint.tipoDte}
                    defaultFormat="80mm"
                    autoPrint
                  />
                )}
      </div>
      
  );
};


export default CobrarPedidos;



